/**
 * @file `<MdxProvider/>` — wraps `@mdx-js/react`'s `MDXProvider` with
 *       course-kit's default component map.
 * @module @labcraft/course-kit/components/provider
 *
 * When a chapter MDX file uses `<Objective>`, `<NextSteps>`, or
 * `<Aside>`, the renderer looks those names up in the surrounding
 * component map and mounts course-kit's implementations. Authors
 * never import these components explicitly.
 *
 * Platforms can extend or override the map by passing additional
 * components via the `components` prop; the default map is merged in
 * underneath.
 */

import type { JSX, ReactNode } from 'react'
import { MDXProvider } from '@mdx-js/react'
import { Aside } from './aside.tsx'
import { Command } from './command.tsx'
import { Era, Evolution, Why } from './evolution.tsx'
import { Glossary } from './glossary.tsx'
import { Key } from './key.tsx'
import { Objective } from './objective.tsx'
import { NextSteps } from './next_steps.tsx'
import { Term } from './term.tsx'
import styles from './provider.module.css'

const DEFAULT_COMPONENTS = {
  Aside,
  Command,
  Era,
  Evolution,
  Glossary,
  Key,
  Objective,
  NextSteps,
  Term,
  Why,
}

export interface MdxProviderProps {
  /** Additional components to merge into the default map. */
  components?: Record<string, unknown>
  children: ReactNode
}

export function MdxProvider(props: MdxProviderProps): JSX.Element {
  const merged = {
    ...DEFAULT_COMPONENTS,
    ...(props.components ?? {}),
  }
  return (
    <MDXProvider components={merged}>
      <div className={styles.prose}>{props.children}</div>
    </MDXProvider>
  )
}
